import type { Client, Project, Priority, TaskStatus, TaskOrigin } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X } from 'lucide-react';

interface TaskFiltersProps {
  clients: Client[];
  projects: Project[];
  clientFilter: string;
  projectFilter: string;
  priorityFilter: Priority | 'all';
  statusFilter: TaskStatus | 'all';
  originFilter: TaskOrigin | 'all';
  onClientChange: (value: string) => void;
  onProjectChange: (value: string) => void;
  onPriorityChange: (value: Priority | 'all') => void;
  onStatusChange: (value: TaskStatus | 'all') => void;
  onOriginChange: (value: TaskOrigin | 'all') => void;
}

export function TaskFilters({
  clients,
  projects,
  clientFilter,
  projectFilter,
  priorityFilter,
  statusFilter,
  originFilter,
  onClientChange,
  onProjectChange,
  onPriorityChange,
  onStatusChange,
  onOriginChange,
}: TaskFiltersProps) {
  const activeFiltersCount = [
    clientFilter !== 'all',
    projectFilter !== 'all',
    priorityFilter !== 'all',
    statusFilter !== 'all',
    originFilter !== 'all',
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    onClientChange('all');
    onProjectChange('all');
    onPriorityChange('all');
    onStatusChange('all');
    onOriginChange('all');
  };

  const getOriginLabel = (origin: TaskOrigin) => {
    switch (origin) {
      case 'github': return 'GitHub';
      default: return 'Flowky';
    }
  };

  return (
    <>
      {/* Active Filters */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-sm text-muted-foreground">Active filters:</span>

          {clientFilter !== 'all' && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onClientChange('all')}
            >
              Client: {clients.find((c) => c.id === clientFilter)?.name}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}

          {projectFilter !== 'all' && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onProjectChange('all')}
            >
              Project: {projects.find((p) => p.id === projectFilter)?.name}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}

          {priorityFilter !== 'all' && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onPriorityChange('all')}
            >
              Priority: {priorityFilter.charAt(0).toUpperCase() + priorityFilter.slice(1)}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}

          {statusFilter !== 'all' && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onStatusChange('all')}
            >
              Status: {statusFilter === 'not-started' ? 'Not Started' : statusFilter === 'in-progress' ? 'In Progress' : 'Completed'}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}

          {originFilter !== 'all' && (
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => onOriginChange('all')}
            >
              Source: {getOriginLabel(originFilter)}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}

          {activeFiltersCount > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              Clear all
            </Button>
          )}
        </div>
      )}
    </>
  );
}

interface TaskFiltersInlineProps {
  clients: Client[];
  projects: Project[];
  clientFilter: string;
  projectFilter: string;
  priorityFilter: Priority | 'all';
  statusFilter: TaskStatus | 'all';
  onClientChange: (value: string) => void;
  onProjectChange: (value: string) => void;
  onPriorityChange: (value: Priority | 'all') => void;
  onStatusChange: (value: TaskStatus | 'all') => void;
}

export function TaskFiltersInline({
  clients,
  projects,
  clientFilter,
  projectFilter,
  priorityFilter,
  statusFilter,
  onClientChange,
  onProjectChange,
  onPriorityChange,
  onStatusChange,
}: TaskFiltersInlineProps) {
  return (
    <>
      <Select value={clientFilter} onValueChange={onClientChange}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="All Clients" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Clients</SelectItem>
          {clients.map((client) => (
            <SelectItem key={client.id} value={client.id}>
              {client.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={projectFilter} onValueChange={onProjectChange}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="All Projects" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Projects</SelectItem>
          {projects.map((project) => (
            <SelectItem key={project.id} value={project.id}>
              {project.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={priorityFilter}
        onValueChange={(v) => onPriorityChange(v as Priority | 'all')}
      >
        <SelectTrigger className="w-[130px]">
          <SelectValue placeholder="All Priorities" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Priorities</SelectItem>
          <SelectItem value="high">High</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="low">Low</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={statusFilter}
        onValueChange={(v) => onStatusChange(v as TaskStatus | 'all')}
      >
        <SelectTrigger className="w-[130px]">
          <SelectValue placeholder="All Statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="not-started">Not Started</SelectItem>
          <SelectItem value="in-progress">In Progress</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
        </SelectContent>
      </Select>
    </>
  );
}
